import clsx from 'clsx';
import { type CSSProperties } from 'react';

import { extract } from './extract-util.js';
import { GapProps, getGapSprinkles } from './getGapSprinkles.js';
import {
  GridFlexPositionProps,
  getGridFlexPositionSprinkles,
} from './getGridFlexPositionSprinkles.js';
import { LayoutSizeProps, getLayoutSizeStyles } from './getLayoutSizeStyles.js';
import { SpacingProps, getSpacingSprinkles } from './getSpacingSprinkles.js';
import { globalSprinkles } from './safe-sprinkles.js';

/**
 * Defines the flex item style props.
 * @public
 */
export interface FlexItemStyleProps {
  /**
   * Determines whether this is a flex item.
   * If true, display: 'flex' style will not be added.
   */
  flexItem?: boolean;
}

/**
 * Keys that will define the flex item.
 * @public
 */
const flexItemPropKeys: Array<keyof FlexItemStyleProps> = ['flexItem'];

/**
 * Defines the flex props.
 * @public
 */
export interface FlexStyleProps
  extends
    GridFlexPositionProps,
    GapProps,
    LayoutSizeProps,
    SpacingProps,
    FlexItemStyleProps {
  /* Flex item props */
  /** Defines order */
  order?: CSSProperties['order'];
  /** Defines flex grow */
  flexGrow?: CSSProperties['flexGrow'];
  /** Defines flex shrink */
  flexShrink?: CSSProperties['flexShrink'];
  /** Defines flex basis */
  flexBasis?: CSSProperties['flexBasis'];
  /** Defines flex */
  flex?: CSSProperties['flex'];
  /* Flex container props */
  /** Defines flex direction */
  flexDirection?: CSSProperties['flexDirection'];
  /** Defines flex wrap */
  flexWrap?: CSSProperties['flexWrap'];
  /** Defines flex flow */
  flexFlow?: CSSProperties['flexFlow'];
  /* Grid item props */
  /** Defines grid column start */
  gridColumnStart?: CSSProperties['gridColumnStart'];
  /** Defines grid column end */
  gridColumnEnd?: CSSProperties['gridColumnEnd'];
  /** Defines grid row start */
  gridRowStart?: CSSProperties['gridRowStart'];
  /** Defines grid row end */
  gridRowEnd?: CSSProperties['gridRowEnd'];
  /** Defines grid column */
  gridColumn?: CSSProperties['gridColumn'];
  /** Defines grid row */
  gridRow?: CSSProperties['gridRow'];
  /** Defines grid area */
  gridArea?: CSSProperties['gridArea'];
}

/**
 * Keys that will define the flex.
 * @public
 */
const flexStylePropKeys: Array<keyof FlexStyleProps> = [
  /* Flex item props */
  'order',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'flex',
  /* Grid container props */
  'flexDirection',
  'flexWrap',
  'flexFlow',
  /* Grid item props */
  'gridColumnStart',
  'gridColumnEnd',
  'gridRowStart',
  'gridRowEnd',
  'gridColumn',
  'gridRow',
  'gridArea',
];

// This one is split out of the getFlexStyles to ease typing, as the generic
// would be overly complex when using inline.
/**
 * Extracts the flexItem prop from the remaining props.
 * @public
 */
function extractFlexItem<T extends FlexItemStyleProps>(
  props: T,
): {
  extracted: Pick<T, keyof FlexItemStyleProps>;
  rest: Omit<T, keyof FlexItemStyleProps>;
} {
  const { extracted, rest } = extract(props, flexItemPropKeys);
  return { extracted, rest };
}

/**
 * Helper function that will extract all styling relevant props for the Flex styling
 * and will return them as classnames and style attributes.
 * The remaining props returned are the ones that no longer contain any Flex styling
 * related entries.
 * @public
 */
export function getFlexStyles<T extends FlexStyleProps>(
  props: T,
  defaults: Partial<FlexStyleProps> = {},
): [
  className: string,
  style: CSSProperties,
  remainingProps: Omit<T, keyof FlexStyleProps>,
] {
  const { extracted: additionalFlexStyles, rest } = extract(
    props,
    flexStylePropKeys,
  );

  const [sizeStyles, afterSizeExtraction] = getLayoutSizeStyles(rest, defaults);

  const [gridFlexClassNames, gridFlexRemainingProps] =
    getGridFlexPositionSprinkles(afterSizeExtraction, defaults);

  const [spacingClassNames, spacingRemainingProps] = getSpacingSprinkles(
    gridFlexRemainingProps,
    defaults,
  );

  const [gapClassNames, gapRemainingProps] = getGapSprinkles(
    spacingRemainingProps,
    defaults,
  );

  const { extracted: flexItemProps, rest: remainingProps } =
    extractFlexItem(gapRemainingProps);

  const flexItemClassName = flexItemProps.flexItem
    ? undefined
    : globalSprinkles({ display: 'flex' });

  return [
    clsx(
      gridFlexClassNames,
      spacingClassNames,
      gapClassNames,
      flexItemClassName,
    ),
    {
      ...sizeStyles,
      ...additionalFlexStyles,
    },
    remainingProps as Omit<T, keyof FlexStyleProps>,
  ];
}
